import { Grid } from '@mui/material';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import { useMemo, ReactElement } from 'react';

import { useGetStatesQuery } from '../../../../api/reference-data';
import { FormikAutoCompleteField } from '../../../../general/inputs/formik-auto-complete-field';
import { FormikTextField } from '../../../../general/inputs/formik-text-field';

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikHandlers & FormikState<any> & FormikHelpers<any>;
  readOnly?: boolean;
  objectPrefix?: objectPrefix;
  streetAddressLabelOverride?: string;
  // When true, marks the street address inputs as data-public for LogRocket.
  // Use for business addresses; leave default (false) for personal/officer addresses.
  isPublic?: boolean;
};

type objectPrefix = 'personalInfo';

function AddressFormFields({
  formik,
  readOnly,
  objectPrefix,
  streetAddressLabelOverride,
  isPublic = false,
}: Props): ReactElement {
  const { data: states } = useGetStatesQuery();
  const streetAddressLabel = useMemo(() => {
    if (streetAddressLabelOverride) {
      return streetAddressLabelOverride;
    }

    if (objectPrefix === 'personalInfo') {
      return 'Residential Street Address';
    }

    return 'Street Address';
  }, [objectPrefix, streetAddressLabelOverride]);

  const prefix = objectPrefix ? `${objectPrefix}.` : '';
  return (
    <>
      <FormikTextField
        formik={formik}
        id="streetAddress"
        name={`${prefix}address.streetAddress`}
        label={streetAddressLabel}
        placeholder={streetAddressLabel}
        readOnly={readOnly}
        data-public={isPublic || undefined}
      />

      <FormikTextField
        formik={formik}
        id="streetAddress2"
        name={`${prefix}address.streetAddress2`}
        label="Unit or C/O"
        placeholder="Unit or C/O"
        readOnly={readOnly}
        data-public={isPublic || undefined}
      />

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <FormikTextField
            formik={formik}
            id="city"
            name={`${prefix}address.city`}
            label="City"
            placeholder="City"
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <FormikAutoCompleteField
            formik={formik}
            id="state"
            name={`${prefix}address.state`}
            fullWidth
            label="State"
            options={states ?? []}
            readOnly={readOnly}
            autoSelect
            autoHighlight
          />
        </Grid>

        <Grid item xs={4}>
          <FormikTextField
            formik={formik}
            id="zip"
            name={`${prefix}address.zip`}
            label="Zip"
            placeholder="Zip"
            readOnly={readOnly}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
      </Grid>
    </>
  );
}

export { AddressFormFields };
